import {
  Check as CheckIcon,
  Close
} from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  challanAutomateAllocation,
  deleteChallanData,
  downloadVendorChallan,
  editVendorChallanDetails,
  getAllocatePriorChallanStatus,
  getChallanAllocation,
  releaseAllocatedChallan,
  searchVendorChallanList,
} from '../../../api/allcoation.js'
import { saveBlobAsFile } from '../../../api/apiUtils.js'
import {
  ColumnConfigurationIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  SearchIconBtn
} from '../../../assets/icons/index'
import Search from '../../../components/common/Search.jsx'
import {
  allocationRowSchema,
  columns,
  searchFields,
} from '../../../constants/allocateColumn.jsx'
import { GLOBAL_CONSTANTS } from '../../../constants/globalConstants.js'
import {
  DEFAULT_PAGE,
  getValidCriteria,
  sanitizeForLog,
} from '../../../constants/index.js'
import { useSnackbar } from '../../../contexts/SnackbarProvider'
import { fetchEntityListIfNeeded } from '../../../store/actions/entityTanActions.js'
import { ROLE_CAPABILITIES } from '../../../utils/role-capabilities.js'
import ColumnConfiguration from '../../common/columnConfiguration.jsx'
import CommonTable from '../../common/CommonTable'
import ConfirmationDialog from '../../common/ConfirmationDialog.jsx'
import CustomSelect from '../CustomSelect.jsx'
import KeyFilter from '../Define/common/KeyFilter'

function Allocate() {
  const { showSnackbar } = useSnackbar()
  const [editMode, setEditMode] = useState(false)
  const [editingData, setEditingData] = useState({})
  const [selectedRows, setSelectedRows] = useState([])
  const roleTypes = useSelector(state => state.user.role)
  const roleConfig = ROLE_CAPABILITIES[roleTypes] || ROLE_CAPABILITIES.default
  const [loading, setLoading] = useState(false)
  const [defaultSearchCOList, setDefaultSearchCOList] = useState([])
  const dispatch = useDispatch()
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false)

  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])

  // pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [tanMasterUuid, setTanMasterUuid] = useState(null)

  // column configuration
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] = useState(null)
  const [visibleColumns, setVisibleColumns] = useState(
    columns.map(col => col.id)
  )

  const [allocationTableData, setAllocationTableData] = useState([])
  const [challanAllocationBasisList, setChallanAllocationBasisList] = useState(
    []
  )
  const [selectedChallanBasis, setSelectedChallanBasis] = useState('')
  const [rowErrors, setRowErrors] = useState({})
  const [showTable, setShowTable] = useState(false)

  const fetchAllocationList = async ({
    page = 1,
    pageSize = itemsPerPage,
    searchList = [],
    tanId = tanUuid,
    defaultSearch = defaultSearchCOList,
  }) => {
    setLoading(true)

    const { tan, allocateFy, allocationStatus, entityName } =
      defaultSearch || {}

    const payload = {
      pageFilter: {
        currentPage: page,
        includeCount: true,
        pageSize: pageSize,
        sortField: 'dateOfTaxDeposited',
        sortOrder: 'DESC',
      },
      searchCOList: searchList,
      entityTan: tan,
      entityName: entityName,
      financialYear: allocateFy ? allocateFy : [],
      allocationStatus,
      userRoleType: 'ADMIN',
    }

    try {
      // Hit both APIs in parallel
      const response = await searchVendorChallanList(payload, tanId)
      if (response?.success) {
        setAllocationTableData(response.data?.content || [])
        setTotalItems(response.data?.totalElements || 0)
      } else {
        setAllocationTableData([])
      }
    } catch (error) {
      console.error(
        'Error loading allocation list',
        sanitizeForLog(error?.message || 'Unknown error')
      )
      setAllocationTableData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchAllocationBasis = async () => {
      const resp = await getChallanAllocation()
      if (resp.success) {
        setChallanAllocationBasisList(resp.data)
      }
    }
    fetchAllocationBasis()
  }, [])

  const filteredColumns = useMemo(
    () => columns.filter(col => visibleColumns.includes(col.id)),
    [visibleColumns]
  )

  const handleSelectRow = useCallback(id => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    )
  }, [])

  const handleSelectAll = event => {
    setSelectedRows(
      event.target.checked ? allocationTableData.map(row => row.uuid) : []
    )
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }

  const handleSearch = async event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)

    await fetchAllocationList({
      page: 1,
      pageSize: itemsPerPage,
      searchList,
      tanId: tanMasterUuid,
      defaultSearch: defaultSearchCOList,
    })

    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    setSearchOpen(false)
    await fetchAllocationList({
      page: 1,
      pageSize: itemsPerPage,
      searchList: [],
      tanId: tanMasterUuid,
      defaultSearch: defaultSearchCOList,
    })
  }

  useEffect(() => {
    const payload = {
      pageFilter: {
        currentPage: 1,
        includeCount: true,
        pageSize: DEFAULT_PAGE,
        sortField: 'entityName',
        sortOrder: 'ASC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: [],
    }
    if (roleConfig.canCreate) {
      dispatch(fetchEntityListIfNeeded(payload))
    }
  }, [dispatch, roleConfig.canCreate])

  const handleViewClick = async data => {
    setTanMasterUuid(data.tanMasterUuid)
    setEditMode(false)
    setDefaultSearchCOList(data)

    await fetchAllocationList({
      page: 1,
      pageSize: itemsPerPage,
      searchList: [],
      tanId: data.tanMasterUuid,
      defaultSearch: data,
    })
    setShowTable(true)
  }

  const handlePageChange = async page => {
    setCurrentPage(page)
    await fetchAllocationList({
      page,
      pageSize: itemsPerPage,
      searchList: getValidCriteria(searchCriteria),
      tanId: tanMasterUuid,
      defaultSearch: defaultSearchCOList,
    })
  }

  const handleItemsPerPageChange = async newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    await fetchAllocationList({
      page: 1,
      pageSize: newItemsPerPage,
      searchList: getValidCriteria(searchCriteria),
      tanId: tanMasterUuid,
      defaultSearch: defaultSearchCOList,
    })
  }

  const handleColumnConfigOpen = event => {
    setColumnConfigAnchor(event.currentTarget)
    setColumnConfigOpen(!columnConfigOpen)
  }

  const handleColumnChange = selectedColumns => {
    setVisibleColumns(selectedColumns)
  }

  const handleColumnConfigurationClose = () => {
    setColumnConfigOpen(false)
    setColumnConfigAnchor(null)
  }

  const handleCloseEditMode = () => {
    setEditMode(false)
    setEditingData({})
  }

  const handleRelease = async () => {
    if (!selectedRows?.length) {
      showSnackbar('Please select at least one challan', 'error')
      return
    }

    const selectedChallans = allocationTableData.filter(row =>
      selectedRows.includes(row.uuid)
    )

    if (!selectedChallans.length) {
      showSnackbar('No valid challans found', 'error')
      return
    }

    const invalidStatusRows = selectedChallans.filter(
      row => !['Partial', 'Full'].includes(row.allocationStatus)
    )

    if (invalidStatusRows.length > 0) {
      invalidStatusRows.forEach((row, i) => {
        setTimeout(
          () =>
            showSnackbar(
              `Row ${
                allocationTableData.findIndex(r => r.uuid === row.uuid) + 1
              }: Only Partial or Full allocation can be released`,
              'error'
            ),
          i * 600
        )
      })
      return
    }

    const payload = selectedChallans.map(row => row.uuid)

    const result = await releaseAllocatedChallan(payload)

    if (result?.success) {
      showSnackbar(result.data.data, 'success')
      setSelectedRows([])
      await fetchAllocationList({
        page: currentPage,
        pageSize: itemsPerPage,
        searchList: getValidCriteria(searchCriteria),
        tanId: tanMasterUuid,
        defaultSearch: defaultSearchCOList,
      })
    } else {
      showSnackbar(result?.error || 'Release failed', 'error')
    }
  }

  const getSelectedChallans = (allocationTableData, selectedRows) =>
    allocationTableData.filter(row => selectedRows.includes(row.uuid))

  const validateSelectedChallans = ({
    selectedRows,
    allocationTableData,
    showSnackbar,
  }) => {
    if (!selectedRows?.length) {
      showSnackbar('Please select at least one challan', 'error')
      return null
    }

    const selectedChallans = getSelectedChallans(
      allocationTableData,
      selectedRows
    )

    if (!selectedChallans.length) {
      showSnackbar('No valid challans found', 'error')
      return null
    }

    const invalidStatusRows = selectedChallans.filter(
      row => !['Partial', 'New'].includes(row.allocationStatus)
    )

    if (invalidStatusRows.length) {
      invalidStatusRows.forEach((row, i) => {
        setTimeout(() => {
          showSnackbar(
            `Row ${
              allocationTableData.findIndex(r => r.uuid === row.uuid) + 1
            }: Only Partial or New challans can be auto-allocated`,
            'error'
          )
        }, i * 600)
      })
      return null
    }

    return selectedChallans
  }

  const executeAutoAllocation = async ({
    payload,
    challanAutomateAllocation,
    showSnackbar,
    onSuccess,
  }) => {
    const result = await challanAutomateAllocation(payload)

    if (result?.success) {
      showSnackbar(result?.data?.data, 'success')
      await onSuccess?.()
    } else {
      showSnackbar(result?.error || 'Auto allocation failed', 'error')
    }
  }

  const handleConfirmAllocate = async () => {
    const { tan } = defaultSearchCOList || {}

    const selectedChallans = validateSelectedChallans({
      selectedRows,
      allocationTableData,
      showSnackbar,
    })
    if (!selectedChallans) return

    const payload = {
      vendorChallanUuid: selectedChallans.map(row => row.uuid),
      entityTan: tan,
      allocationChallanBasis: selectedChallanBasis,
    }

    await executeAutoAllocation({
      payload,
      challanAutomateAllocation,
      showSnackbar,
      onSuccess: async () => {
        setSelectedRows([])
        setSelectedChallanBasis('')
        setAllocateDialogOpen(false)

        await fetchAllocationList({
          page: currentPage,
          pageSize: itemsPerPage,
          searchList: getValidCriteria(searchCriteria),
          tanId: tanMasterUuid,
          defaultSearch: defaultSearchCOList,
        })
      },
    })
  }

  const handleAutoAllocate = async () => {
    const { tan, allocateFy } = defaultSearchCOList || {}

    const selectedChallans = validateSelectedChallans({
      selectedRows,
      allocationTableData,
      showSnackbar,
    })
    if (!selectedChallans) return

    const challanUuids = selectedChallans.map(row => row.uuid)

    const priorResp = await getAllocatePriorChallanStatus(
      challanUuids,
      tanMasterUuid,
      allocateFy
    )

    if (priorResp?.success && priorResp?.data) {
      setAllocateDialogOpen(true)
      return
    }

    const payload = {
      vendorChallanUuid: challanUuids,
      entityTan: tan,
      allocationChallanBasis: selectedChallanBasis,
    }

    await executeAutoAllocation({
      payload,
      challanAutomateAllocation,
      showSnackbar,
      onSuccess: async () => {
        setSelectedRows([])
        setSelectedChallanBasis('')

        await fetchAllocationList({
          page: currentPage,
          pageSize: itemsPerPage,
          searchList: getValidCriteria(searchCriteria),
          tanId: tanMasterUuid,
          defaultSearch: defaultSearchCOList,
        })
      },
    })
  }

  const handleChallanChange = event => {
    setSelectedChallanBasis(event.target.value)
  }

  // Field change handler
  const handleFieldChange = (rowId, field, value) => {
    setEditingData(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [field]: value,
      },
    }))
  }

  const handleEdit = () => {
    setEditingData({})
    setEditMode(true)
  }

  const handleSaveEdit = async () => {
    const entries = Object.entries(editingData || {})

    if (!entries.length) {
      showSnackbar('No rows to save', 'error')
      return
    }

    const validRowsPayload = []
    const invalidMessages = []

    entries.forEach(([uuid, editedFields]) => {
      const tableRow = allocationTableData.find(row => row.uuid === uuid)

      if (!tableRow) {
        invalidMessages.push(`Row ${uuid}: Row data not found`)
        return
      }

      const rowIndex = allocationTableData.findIndex(row => row.uuid === uuid)
      const rowNumber = rowIndex + 1

      const mergedRow = {
        ...tableRow,
        ...editedFields,
      }

      const normalizedRow = {
        uuid: mergedRow.uuid,
        year: String(mergedRow.year ?? ''),
        challanSerialNo: mergedRow.challanSerialNo ?? '',
        bsrCode: String(mergedRow.bsrCode ?? ''),

        dateOfTaxDeposited: mergedRow.dateOfTaxDeposited,

        tdsDepositedByBookEntry: mergedRow.tdsDepositedByBookEntry ?? 'No',
        corporateNonCorporate:
          mergedRow.corporateNonCorporate ?? 'Non-corporate',

        tds: String(mergedRow.tds ?? ''),
        surcharge: String(mergedRow.surcharge ?? '0'),
        educationCess: String(mergedRow.educationCess ?? '0'),
        interest: String(mergedRow.interest ?? '0'),
        fee: String(mergedRow.fee ?? '0'),
        others: String(mergedRow.others ?? '0'),
        totalTaxDeposited: String(mergedRow.totalTaxDeposited ?? ''),
        sectionAsPerChallan: mergedRow.sectionAsPerChallan ?? '',
        minorHeadOfChallan: mergedRow.minorHeadOfChallan ?? '',
        allocationStatus: mergedRow.allocationStatus.toUpperCase(),
        manualAdjustment: String(mergedRow.manualAdjustment ?? '0'),
      }

      const result = allocationRowSchema.safeParse(normalizedRow)

      if (!result.success) {
        result.error.issues.forEach(issue => {
          const field = issue.path?.[0] ?? 'unknown'
          invalidMessages.push(`Row ${rowNumber} [${field}]: ${issue.message}`)
        })
        return
      }

      validRowsPayload.push(normalizedRow)
    })

    if (invalidMessages.length) {
      invalidMessages.forEach((msg, i) =>
        setTimeout(() => showSnackbar(msg, 'error'), i * 600)
      )
      return
    }

    if (!validRowsPayload.length) {
      showSnackbar('No valid changes to update', 'error')
      return
    }

    const result = await editVendorChallanDetails(validRowsPayload)

    if (result?.success && result?.data?.success) {
      showSnackbar('Updated successfully', 'success')

      const updatedEditingData = { ...editingData }
      validRowsPayload.forEach(r => delete updatedEditingData[r.uuid])
      setEditingData(updatedEditingData)

      setSelectedRows([])
      setEditMode(false)
      await fetchAllocationList({
        page: currentPage,
        pageSize: itemsPerPage,
        searchList: getValidCriteria(searchCriteria),
        tanId: tanMasterUuid,
        defaultSearch: defaultSearchCOList,
      })
    } else {
      const errorMessage = result?.data?.errors?.[0]?.message || 'Save failed'
      showSnackbar(errorMessage, 'error')
    }
  }

  const handleBulkDownload = async () => {
    // Check if there's no data to download
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    setIsDownloadLoading(true)
    const { tan, allocationStatus, allocateFy, entityName } =
      defaultSearchCOList || {}

    const payload = {
      pageFilter: {
        currentPage: currentPage,
        includeCount: true,
        pageSize: DEFAULT_PAGE,
        sortField: 'dateOfTaxDeposited',
        sortOrder: 'DESC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: [],
      entityTan: tan,
      entityName: entityName,
      financialYear: allocateFy,
      allocationStatus,
    }
    const result = await downloadVendorChallan(payload, tanMasterUuid)
    if (result?.success && result?.data)
      saveBlobAsFile(result.data, 'allocation_challan.xlsx')
    else if (result?.error) showSnackbar(result.error, 'error')
    setIsDownloadLoading(false)
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return
    const payload = allocationTableData
      .filter(item => selectedRows.includes(item.uuid))
      .map(item => item.uuid)
    const res = await deleteChallanData(payload)
    if (res.success) {
      showSnackbar(res.data.data, 'success')
      // Update local table state
      await fetchAllocationList({
        page: currentPage,
        pageSize: itemsPerPage,
        searchList: getValidCriteria(searchCriteria),
        tanId: tanMasterUuid,
        defaultSearch: defaultSearchCOList,
      })
      setSelectedRows([])
    } else {
      showSnackbar(res?.error || 'Delete failed', 'error')
    }
  }

  return (
    <>
      {/* Key Filters */}
      <KeyFilter handleViewClick={handleViewClick} isAllocateStatus={true} />

      {showTable && (
        <>
          {/* Mid Section Title and Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: '32px',
              py: '12px',
            }}
            component="section"
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                color: 'var(--forbrand-secondary)',
                fontWeight: 400,
                fontFamily: 'ch',
              }}
            >
              Allocate Challans
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Column Configuration">
                <IconButton onClick={handleColumnConfigOpen}>
                  <ColumnConfigurationIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download Excel">
                <IconButton
                  disabled={isDownloadLoading}
                  onClick={handleBulkDownload}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              {selectedRows?.length > 0 ? (
                <Tooltip title={GLOBAL_CONSTANTS.DELETE}>
                  <IconButton
                    sx={{
                      color: 'var(--forbrand-primary)',
                    }}
                    onClick={handleBulkDelete}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              ) : null}

              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  sx={{
                    color: editMode
                      ? 'var(--forbrand-primary)'
                      : 'var(--forbrand-primary)',
                  }}
                  onClick={editMode ? handleSaveEdit : handleEdit}
                >
                  {editMode ? <CheckIcon /> : <EditIcon />}
                </IconButton>
              </Tooltip>
              {editMode && (
                <Tooltip title="Cancel">
                  <IconButton
                    sx={{
                      color: 'var(--forbrand-primary)',
                    }}
                    onClick={handleCloseEditMode}
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Advance Search">
                <IconButton onClick={handleSearch}>
                  <SearchIconBtn />
                </IconButton>
              </Tooltip>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: 16,
                  margin: '9px 4px 0 2px',
                  color: 'var(--forbrand-color)',
                }}
              />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: 'var(--forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  px: '16px',
                  py: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
                onClick={handleRelease}
                disabled={selectedRows.length == 0}
              >
                Release Allocation
              </Button>
            </Box>
          </Box>

          {/* Allocation Basis Selection */}
          <Box
            className="bg-white "
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <CustomSelect
              label="Challan Allocation Basis"
              value={selectedChallanBasis}
              onChange={handleChallanChange}
              options={challanAllocationBasisList}
              getOptionLabel={option => option}
              getOptionValue={option => option}
              placeholder={'Select Challan Allocation Basis'}
              disabled={challanAllocationBasisList.length == 0}
            />
            <Button
              variant="contained"
              sx={{
                backgroundColor: 'var(--forbrand-bg-variant)',
                color: 'var(--forbrand-brand-varient)',
                px: '16px',
                py: '8px',
                textTransform: 'none',
                fontWeight: 500,
                mt: 3,
              }}
              disabled={
                selectedChallanBasis.length === 0 || selectedRows.length === 0
              }
              onClick={handleAutoAllocate}
            >
              Allocate
            </Button>
          </Box>

          {/* Data Table */}
          <Box className="mt-[16px]">
            <CommonTable
              columns={filteredColumns}
              data={allocationTableData}
              loading={loading}
              editMode={editMode}
              editingData={editingData}
              selectedItems={selectedRows}
              onSelectItem={handleSelectRow}
              onSelectAll={handleSelectAll}
              showCheckbox={true}
              onDelete={{}}
              emptyMessage="No data found"
              showFooter={true}
              showActions={false}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={handlePageChange}
              currentTable="allocation_page"
              onFieldChange={handleFieldChange}
              totalItems={totalItems}
            />
          </Box>
        </>
      )}

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />

      <ConfirmationDialog
        open={allocateDialogOpen}
        onClose={() => setAllocateDialogOpen(false)}
        onConfirm={handleConfirmAllocate}
        title="Allocate Challan"
        message={
          'Prior Period Challan(s) are available for allocation. Allocating selected challan(s) might bring interest implications. Do you want to continue'
        }
        confirmText="Proceed"
        type="delete"
        itemType="item"
        showCancelButton={true}
        onCancel={() => setAllocateDialogOpen(false)}
      />

      <ColumnConfiguration
        columns={columns}
        visibleColumns={visibleColumns}
        anchorEl={columnConfigAnchor}
        open={columnConfigOpen}
        onColumnChange={handleColumnChange}
        onClose={handleColumnConfigurationClose}
      />
    </>
  )
}

export default Allocate
